import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { VideoPlayer } from "@/components/VideoPlayer";
import { Button } from "@/components/ui/button";
import {
  Cube, QrCode, MapPin, ArrowsLeftRight, ShieldCheck, DeviceMobile,
  ChartLineUp, ArrowRight, Check, Buildings, Car,
  HardHat, Truck, Laptop, House, Play, FirstAidKit,
} from "@phosphor-icons/react";


const VideoTeaser = () => {
  const [videos, setVideos] = useState([]);
  useEffect(() => { api.get("/videos").then(({ data }) => setVideos(data.slice(0, 3))).catch(() => {}); }, []);
  if (videos.length === 0) return null;
  return (
    <section className="border-y border-zinc-200 bg-white" data-testid="video-teaser">
      <div className="max-w-7xl mx-auto px-5 py-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 mb-3">See it in action</p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Watch Orbit at work.</h2>
          </div>
          <Link to="/videos" className="inline-flex items-center gap-1.5 font-semibold text-emerald-600 hover:text-emerald-700 transition-colors" data-testid="video-teaser-all">See all videos <ArrowRight size={18} weight="bold" /></Link>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((v) => (
            <div key={v.id} className="rounded-2xl border border-zinc-200 bg-[#FAFAFB] overflow-hidden hover:border-emerald-300 hover:shadow-lg hover:shadow-zinc-200/60 transition-all" data-testid={`video-teaser-${v.id}`}>
              <div className="aspect-video bg-black"><VideoPlayer video={v} /></div>
              <div className="p-5">
                <p className="text-xs uppercase tracking-[0.18em] font-semibold text-emerald-600 mb-1">{v.category || "General"}</p>
                <h3 className="font-display font-bold text-lg leading-snug">{v.title}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


const FEATURES = [
  { icon: QrCode, title: "QR Code Tracking", desc: "Every asset gets a unique, secure QR code. Scan to instantly pull up the full profile from any phone." },
  { icon: ArrowsLeftRight, title: "Movement Ledger", desc: "An append-only history log records every location change — who moved it, when, and why. Nothing is ever lost." },
  { icon: MapPin, title: "Location Management", desc: "Main Lot, Service, Body Shop, Detail, Storage and more. See live asset counts per location." },
  { icon: ShieldCheck, title: "Role-Based Access", desc: "Super Admin, Company Admin, Manager, Employee and Read-Only roles with strict company data isolation." },
  { icon: ChartLineUp, title: "Reports & Export", desc: "Inventory, movement, status, missing-asset and activity reports — all exportable to CSV." },
  { icon: DeviceMobile, title: "Built For The Field", desc: "Large touch targets and a fast scan-first mobile workflow made for the lot, the shop, and the road." },
];

const USE_CASES = [
  { icon: Car, title: "Auto Dealerships", statement: "Every vehicle. Every move. On record.", desc: "Track vehicle inventory across the lot & help employees or customers have access to vehicle information.", tags: "Vehicles • Stock Numbers • VIN Tracking" },
  { icon: HardHat, title: "Construction Sites", statement: "Track tools and equipment across every job site.", desc: "Track tools, scaffolding, safety equipment, and machinery across multiple job sites.", tags: "Power Tools • Heavy Equipment • Safety Gear" },
  { icon: Truck, title: "Trailer Management", statement: "Tag it. Track it. Never lose it.", desc: "Tag trailers and mobile equipment. Track service history and assign drivers.", tags: "Trailers • Service Trucks" },
  { icon: House, title: "Real Estate", statement: "One scan from the curb to the full listing.", desc: "Orbit labels can work for homes for sale, mobile homes, apartments, rentals, commercial properties, land, new construction, and more.", tags: "Homes • Rentals • Commercial • Land" },
  { icon: FirstAidKit, title: "Medical Equipment Tracking", statement: "Scan any device. See its inspection history.", desc: "Clinics and healthcare providers use QR labels to track equipment, inspections, and maintenance.", tags: "Devices • Inspections • Maintenance" },
];

const ANNUAL_DISCOUNT = 0.15;
const annualOf = (monthly) => Math.round(monthly * 12 * (1 - ANNUAL_DISCOUNT) * 100) / 100;
const money = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PLANS = [
  { name: "Basic", base: 49, features: ["No per-asset fees", "QR codes & labels", "Basic reports"] },
  { name: "Essential", base: 149, features: ["No per-asset fees", "QR codes & labels", "Advanced reports + CSV", "Movement tracking"] },
  { name: "Pro", base: 299, popular: true, features: ["No per-asset fees", "QR codes & labels", "All reports + CSV", "Movement tracking", "Priority support"] },
  { name: "Enterprise", price: "Custom", unit: "", features: ["From $499 / month", "Unlimited Assets & Users", "Custom asset types", "Dedicated support", "API access"] },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAFAFB] text-zinc-950">
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/orbit-icon.jpg" alt="Orbit" className="h-9 w-9 object-contain rounded-md" />
            <span className="font-display font-bold text-sm tracking-tight"><span className="sm:hidden">Orbit</span><span className="hidden sm:inline">Orbit Inventory Management</span></span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/pricing"><Button variant="ghost" className="h-10 font-semibold hidden sm:inline-flex" data-testid="nav-pricing">Pricing</Button></Link>
            <Link to="/videos"><Button variant="ghost" className="h-10 font-semibold hidden sm:inline-flex" data-testid="nav-videos">Videos</Button></Link>
            <Link to="/get-demo"><Button variant="ghost" className="h-10 font-semibold hidden sm:inline-flex" data-testid="nav-demo">Book a Demo</Button></Link>
            <Link to="/login"><Button variant="ghost" className="h-10 font-semibold" data-testid="nav-login">Sign In</Button></Link>
            <Link to="/register"><Button className="h-10 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="nav-register">Start Free</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 pt-16 lg:pt-24 [container-type:inline-size]">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-700 mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Physical Asset & Inventory Platform
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.9cqw] font-extrabold tracking-tight leading-[0.95] uppercase flex flex-col lg:flex-row lg:flex-nowrap lg:justify-between lg:items-baseline gap-y-1 w-full" data-testid="hero-heading">
            <span>Scan.</span><span className="text-emerald-600">Learn.</span><span>Track.</span><span className="text-emerald-600 lg:whitespace-nowrap">Move Forward.</span>
          </h1>
        </div>
        <div className="max-w-7xl mx-auto px-5 pt-8 pb-16 lg:pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-lg text-zinc-600 leading-relaxed max-w-lg">
              Orbit Inventory Management is a physical asset and inventory platform that helps businesses place a custom QR label on their inventory to connect each item directly to its digital information. Customers & Employees simply scan with a smartphone or tablet, with no special app required.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register"><Button className="h-14 px-8 text-base bg-emerald-500 hover:bg-emerald-600 text-white font-semibold gap-2 w-full sm:w-auto" data-testid="hero-start-button">Start Free Trial <ArrowRight size={20} weight="bold" /></Button></Link>
              <Link to="/get-demo"><Button variant="outline" className="h-14 px-8 text-base border-zinc-300 font-semibold w-full sm:w-auto" data-testid="hero-demo-button">Book a Demo</Button></Link>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-500">
              <span className="flex items-center gap-1.5"><Check size={16} weight="bold" className="text-emerald-500" /> No credit card required</span>
              <span className="flex items-center gap-1.5"><Check size={16} weight="bold" className="text-emerald-500" /> Setup in minutes</span>
            </div>
            <div className="mt-10 flex gap-10 border-t border-zinc-200 pt-6" data-testid="hero-stats">
              <div>
                <p className="font-display text-3xl font-extrabold tracking-tight">2.1M+</p>
                <p className="text-sm text-zinc-500 mt-1">Movements Logged</p>
              </div>
            </div>
          </div>
          <div className="relative" data-testid="intro-band">
            <div className="mb-6">
              <p className="text-base text-zinc-600 leading-relaxed">
                Orbit Inventory Management gives you the tools to track, manage, and maintain your equipment, vehicles, and physical assets—all in one place.
              </p>
            </div>
            <div className="rounded-xl overflow-hidden border border-zinc-200 shadow-2xl shadow-zinc-300/40 bg-white" data-testid="hero-industries-image">
              <img src="/hero-industries.jpg" alt="One solution, multiple industries — custom QR code labels for real estate, dealerships, equipment, rentals, construction, property managers and medical equipment" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing teaser strip */}
      <section className="border-y border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 py-5 flex flex-col sm:flex-row items-center justify-center gap-x-4 gap-y-2 text-center" data-testid="pricing-teaser">
          <span className="text-sm sm:text-base text-zinc-600">
            <span className="font-semibold text-zinc-900">Basic</span> plan from <span className="text-zinc-400 line-through">$99</span>{" "}
            <span className="font-display font-extrabold text-zinc-950 text-lg sm:text-xl">$49</span>
            <span className="text-zinc-500">/mo</span>
            <span className="ml-2 inline-block text-[11px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 rounded px-2 py-0.5 align-middle">Limited offer</span>
          </span>
          <Link to="/pricing" className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 hover:text-emerald-700 transition-colors" data-testid="teaser-pricing-link">
            See full pricing <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>


      {/* Use cases */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 py-16">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 mb-3">Built for any inventory</p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">One platform, endless possibilities.</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {USE_CASES.map((u) => (
              <Link to={`/videos?cat=${encodeURIComponent(u.title)}`} key={u.title} className="group block p-7 rounded-2xl border border-zinc-200 bg-[#FAFAFB] hover:border-emerald-300 hover:shadow-lg hover:shadow-zinc-200/60 transition-all" data-testid={`usecase-${u.title}`}>
                <div className="h-16 w-16 rounded-xl bg-emerald-50 flex items-center justify-center mb-6">
                  <u.icon size={34} weight="duotone" className="text-emerald-600" />
                </div>
                <p className="text-xs uppercase tracking-[0.18em] font-semibold text-emerald-600 mb-2">{u.title}</p>
                <h3 className="font-display font-bold text-xl mb-3 leading-snug">{u.statement}</h3>
                <p className="text-zinc-600 text-base leading-relaxed">{u.desc}</p>
                <div className="flex items-center justify-between gap-3 mt-6 pt-6 border-t border-zinc-200">
                  <p className="text-sm font-semibold text-zinc-400">{u.tags}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 shrink-0 group-hover:gap-2 transition-all"><Play size={14} weight="fill" />Watch</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 mb-3">Everything in one place</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Purpose-built for physical inventory.</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className="p-6 rounded-lg border border-zinc-200 bg-white hover:border-emerald-300 transition-colors">
              <div className="h-11 w-11 rounded-md bg-emerald-50 flex items-center justify-center mb-4">
                <f.icon size={24} weight="bold" className="text-emerald-600" />
              </div>
              <h3 className="font-display font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Videos teaser */}
      <VideoTeaser />

      {/* CTA */}
      <section className="bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto px-5 py-16 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-3xl font-bold tracking-tight">Ready to take control of your inventory?</h2>
            <p className="text-zinc-400 mt-2">Start tracking assets in Seconds.</p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-5">
            <div className="flex flex-col items-center sm:items-end gap-3">
              <a href="tel:+13373595408" className="text-center sm:text-right" data-testid="cta-phone">
                <p className="text-zinc-400 text-sm">Call for More Information</p>
                <p className="font-display text-2xl font-extrabold text-white hover:text-emerald-400 transition-colors">337-359-5408</p>
              </a>
              <a href="mailto:Robert@orbitcodes.com" onClick={() => { navigator.clipboard?.writeText("Robert@orbitcodes.com").then(() => toast.success("Email copied: Robert@orbitcodes.com")).catch(() => {}); }} className="text-center sm:text-right" data-testid="cta-email">
                <p className="text-zinc-400 text-sm">Send us an email</p>
                <p className="font-display text-2xl font-extrabold text-white hover:text-emerald-400 transition-colors">Robert@orbitcodes.com</p>
              </a>
            </div>
            <Link to="/register"><Button className="h-14 px-8 text-base bg-emerald-500 hover:bg-emerald-600 text-white font-semibold gap-2" data-testid="cta-button">Get Started Free <ArrowRight size={20} weight="bold" /></Button></Link>
          </div>
        </div>
      </section>

      <footer className="bg-zinc-950 text-zinc-500 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <span>© {new Date().getFullYear()} Orbit Inventory Management</span>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3">
            <Link to="/pricing" className="hover:text-white transition-colors" data-testid="footer-pricing">Pricing</Link>
            <Link to="/videos" className="hover:text-white transition-colors" data-testid="footer-videos">Videos</Link>
            <Link to="/get-demo" className="hover:text-white transition-colors" data-testid="footer-demo">Book a Demo</Link>
            <Link to="/prospect-score" className="hover:text-white transition-colors" data-testid="footer-prospect">Prospect Scoring</Link>
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms</Link>
            <Link to="/login" className="hover:text-white transition-colors" data-testid="footer-login">Sign In</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
